import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock, LockOpen, Code, CodeXml } from 'lucide-react';
import { KvTable, KvRow } from './KvTable';
import Tooltip from './Tooltip';
import SyntaxHighlightInput from './SyntaxHighlightInput';
import VariableValuePicker from './VariableValuePicker';
import VariableEditorPopup from './VariableEditorPopup';
import { shouldOfferPicker, readOptions } from '../../utils/variableOptions';
import { SUGGEST_DYNAMIC } from '../../utils/variableSuggestions';

/**
 * The variables table — one implementation, used everywhere variables are edited.
 *
 * Environments, Workspace variables and Global variables always rendered this
 * (through EnvironmentEditor); Collection variables rendered a hand-rolled
 * lookalike instead: bare inputs in a flex row, no grid, no borders, no secret
 * toggles. Same idea, different furniture, and the difference was visible the
 * moment you opened both (owner, 22.09.2026 — existing components get reused,
 * a second one-off input does not get written).
 *
 * Collection variables gain the secret controls by arriving here, which is a
 * fix rather than a new feature: `withheldSecretKeys` already walks the
 * collection scope, so a `secret: true` collection variable was ALREADY
 * withheld from scripts — there was simply no way to set the flag.
 *
 * The reveal state lives here because it is pure view state: which masked rows
 * are currently shown is nobody's business but this table's, and it must not
 * survive a save.
 */
export default function VariableKvTable({
  variables = [],
  /**
   * (index, patch) — merge `patch` into that row.
   *
   * A patch rather than (field, value) because marking a row secret changes two
   * fields at once and they must land together: re-locking also clears
   * `scriptAccess`, and a grant that survived half an update would be COL-02
   * quietly undone.
   */
  onUpdate,
  /** (index) */
  onRemove,
  /** (index, value) — key edits are separate: callers re-derive the trailing empty row. */
  onKeyChange,
  disabled = false,
  /**
   * Class on the value picker's portaled menu. Portaled menus live in <body>,
   * so this is how a test reaches the right one when two tables are mounted.
   */
  menuClassName = 'env-option-menu',
  /** Everything SyntaxHighlightInput needs to resolve and highlight `{{vars}}`. */
  inputProps = {},
  /** Extra class per row, e.g. the environment editor's secret styling. */
  rowClassName,
  inputClassName = '',
  inputStyle,
}) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState({});

  /**
   * "Edit options…" from a value's dropdown.
   *
   * The menu listed the alternatives and offered no way to change them, so
   * removing one meant going somewhere else entirely (owner, 23.09.2026). It
   * opens the ordinary variable editor — the same popup a `{{chip}}` opens —
   * rather than a second, table-only way to edit a variable.
   *
   * WITHOUT its scope picker: this table IS one scope, and it has no handler
   * for moving a row out of it. The save goes back through `onUpdate`, so there
   * is still exactly one writer here.
   */
  const [optionsEditor, setOptionsEditor] = useState(null);
  const cellRefs = useRef({});

  const toggleReveal = (id) => setRevealed((r) => ({ ...r, [id]: !r[id] }));

  const toggleSecret = (index, variable) => {
    const isNowSecret = !variable.secret;
    // Re-locking must not silently restore a script grant given in a previous
    // life (COL-02) — the grant is re-earned.
    onUpdate?.(index, { secret: isNowSecret, scriptAccess: false });
    if (!isNowSecret) {
      setRevealed((r) => { const next = { ...r }; delete next[variable._id]; return next; });
    }
  };

  const editor = optionsEditor && (
    <VariableEditorPopup
      key={optionsEditor.name}
      editingVariable={optionsEditor}
      showScope={false}
      variableContext={inputProps.variableContext}
      selectedEnvironment={inputProps.selectedEnvironment}
      workspaceVariables={inputProps.workspaceVariables}
      globalVariables={inputProps.globalVariables}
      collectionVariables={inputProps.collectionVariables}
      folderVariables={inputProps.folderVariables}
      onSave={(value, _scope, _name, options) => {
        // Straight back through `onUpdate` — the popup is the UI for editing a
        // row, not a second thing that writes rows.
        onUpdate?.(optionsEditor.index, { value, options: options || [] });
        setOptionsEditor(null);
      }}
      onClose={() => setOptionsEditor(null)}
    />
  );

  return (
    <>
    <KvTable variant="env">
      {variables.map((variable, index) => {
        const isTrailingEmpty = index === variables.length - 1 && variable.key === '';
        const isRevealed = !!revealed[variable._id];
        const editable = !disabled;

        return (
          <KvRow
            key={variable._id}
            variant="env"
            enabled={variable.enabled}
            onToggleEnabled={editable ? (v) => onUpdate?.(index, { enabled: v }) : undefined}
            rowClassName={[rowClassName, variable.secret ? 'env-var-row--secret' : ''].filter(Boolean).join(' ')}
            showRemove={editable && !isTrailingEmpty}
            onRemove={() => onRemove?.(index)}
            removeDisabled={variables.length === 1}
            renderKey={
              <SyntaxHighlightInput
                expandOnOverflow
                placeholder={t('request.key')}
                value={variable.key}
                disabled={disabled}
                onChange={(e) => onKeyChange?.(index, e.target.value)}
                aria-label={variable.key ? `Name of ${variable.key}` : 'New variable name'}
                showDynamicVarPicker={false}
                className={inputClassName}
                style={inputStyle}
                {...inputProps}
              />
            }
            renderValue={
              // A secret keeps its masked field even when it has options — the
              // picker's menu would list every alternative in plain text.
              shouldOfferPicker(variable) ? (
                <div
                  className="var-value-picker-cell"
                  ref={(node) => { cellRefs.current[variable._id] = node; }}
                >
                  <VariableValuePicker
                    variable={variable}
                    value={variable.value}
                    disabled={disabled}
                    className={inputClassName}
                    menuClassName={menuClassName}
                    ariaLabel={`Value of ${variable.key}`}
                    onChange={(next) => onUpdate?.(index, { value: next })}
                    onEditOptions={
                      disabled
                        ? undefined
                        : () => {
                            const at = cellRefs.current[variable._id]?.getBoundingClientRect();
                            setOptionsEditor({
                              index,
                              name: variable.key,
                              value: variable.value,
                              options: readOptions(variable),
                              exists: true,
                              x: at ? at.left : 0,
                              y: at ? at.bottom : 0,
                            });
                          }
                    }
                  />
                </div>
              ) : variable.secret && !isRevealed ? (
                <input
                  type="password"
                  value={variable.value}
                  disabled={disabled}
                  onChange={(e) => onUpdate?.(index, { value: e.target.value })}
                  placeholder={t('request.value')}
                  className={`${inputClassName} env-var-value-input env-var-value-input--masked`}
                  style={inputStyle}
                />
              ) : (
                <SyntaxHighlightInput
                  // NO `selectionToVariable` here, deliberately. Pulling a
                  // variable out of a variable's own value is a knot, not a
                  // shortcut — you would be defining a variable in terms of one
                  // you are creating in the same breath (owner, 23.09.2026,
                  // after trying it). `variableKvTableMenu.test.js` holds this
                  // down so it does not come back by copying the Params cell.
                  //
                  // `expandOnOverflow` DOES belong: a value too long for one
                  // line is as ordinary here as in a param.
                  expandOnOverflow
                  // Generated values only. Offering the variables of the very
                  // table being edited invites a variable defined in terms of
                  // itself, and `{{$randomUUID}}` as a default IS useful here
                  // (owner, 23.09.2026).
                  suggest={SUGGEST_DYNAMIC}
                  placeholder={t('request.value')}
                  value={variable.value}
                  disabled={disabled}
                  onChange={(e) => onUpdate?.(index, { value: e.target.value })}
                  className={`${inputClassName} env-var-value-input`}
                  style={inputStyle}
                  // Named like the picker branch beside it, so the cell has one
                  // accessible name whichever of the two is rendered.
                  aria-label={`Value of ${variable.key}`}
                  {...inputProps}
                />
              )
            }
            actions={
              editable && !isTrailingEmpty ? (
                <>
                  {variable.secret && (
                    <Tooltip
                      content={isRevealed ? t('environment.hideSecretValue') : t('environment.revealSecretValue')}
                      side="top"
                    >
                      <button
                        type="button"
                        onClick={() => toggleReveal(variable._id)}
                        className="env-var-secret-toggle"
                        tabIndex={-1}
                      >
                        {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </Tooltip>
                  )}
                  {variable.secret && (
                    <Tooltip
                      content={
                        variable.scriptAccess
                          ? 'Scripts can read this secret — click to withhold it again'
                          : 'Scripts cannot read this secret — click to allow (needed for request signing)'
                      }
                      side="top"
                    >
                      <button
                        type="button"
                        onClick={() => onUpdate?.(index, { scriptAccess: !variable.scriptAccess })}
                        className={`env-var-secret-toggle${variable.scriptAccess ? ' env-var-secret-toggle--active' : ''}`}
                        tabIndex={-1}
                      >
                        {variable.scriptAccess ? <CodeXml size={14} /> : <Code size={14} />}
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip
                    content={variable.secret ? t('environment.unmarkSecret') : t('environment.markSecret')}
                    side="top"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSecret(index, variable)}
                      className={`env-var-secret-toggle${variable.secret ? ' env-var-secret-toggle--active' : ''}`}
                      aria-label={variable.secret ? t('environment.unmarkSecret') : t('environment.markSecret')}
                      aria-pressed={Boolean(variable.secret)}
                      tabIndex={-1}
                    >
                      {variable.secret ? <Lock size={14} /> : <LockOpen size={14} />}
                    </button>
                  </Tooltip>
                </>
              ) : null
            }
          />
        );
      })}
    </KvTable>
    {editor}
    </>
  );
}
